/**
 * CANONICAL MODULE — app-platform/shared/errorSession.ts
 *
 * This file is copied byte-for-byte into every app (`src/lib/errorSession.ts`). It imports nothing
 * from any app — only the `ErrorEntry` *type* from `errorLog.ts`, which is itself canonical and
 * byte-identical everywhere, so the two travel together.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place** — that is exactly how
 * `bundle-budget.mjs` became three different files, each with a safeguard the other two never got.
 *
 * ── What it is for ───────────────────────────────────────────────────────────────────────────────
 * `errorLog.ts` keeps a localStorage buffer of entries *not yet flushed to OneDrive*, and
 * `errorFlush.ts` drains it on every successful flush. That buffer is a transport queue, and it is
 * the wrong thing for a Settings panel to read: on a healthy device it is empty precisely because
 * everything worked, so "what went wrong today" would answer "nothing" after the very failure the
 * user came to look up.
 *
 * So this is a second, parallel view with a different lifetime: **everything recorded since this tab
 * loaded, whether or not it has been flushed.** In memory only — it is a read-out, not a record, and
 * `errorFlush` remains the thing that durably reports.
 *
 * Wire it up once at boot, next to the rest of the error config:
 *
 *     configureErrorSync({ ...app facts..., onRecord: recordSessionError })
 *
 * `onRecord` is called inside `errorLog`'s reporter, which must never throw, so every function here
 * is total: no throwing paths, no async, no storage.
 *
 * Then read it from a Settings panel with `useSyncExternalStore`:
 *
 *     useSyncExternalStore(subscribeSessionErrors, sessionErrorsVersion)
 *     const errors = getSessionErrors()
 */

import type { ErrorEntry } from './errorLog'

/** Exported for its test. Entries held for this tab's lifetime. Small: this is a read-out, not the record. */
export const SESSION_CAP = 50

let entries: ErrorEntry[] = []
let version = 0
const subscribers = new Set<() => void>()

/**
 * Pass this as `onRecord` to `configureErrorSync`. Keeps the newest `SESSION_CAP` entries.
 * Never throws — `errorLog`'s reporter guards the call anyway, but a session view that can take
 * the reporter down with it would be worse than no session view.
 */
export function recordSessionError(entry: ErrorEntry): void {
  entries.push(entry)
  if (entries.length > SESSION_CAP) entries = entries.slice(entries.length - SESSION_CAP)
  version += 1
  for (const fn of subscribers) {
    try {
      fn()
    } catch {
      /* one bad subscriber must not stop the others, or the reporter */
    }
  }
}

/** Everything recorded this session, **newest first** — the order a panel reads them in. */
export function getSessionErrors(): ErrorEntry[] {
  return entries.slice().reverse()
}

/** `useSyncExternalStore` subscribe. Returns the unsubscribe function. */
export function subscribeSessionErrors(fn: () => void): () => void {
  subscribers.add(fn)
  return () => {
    subscribers.delete(fn)
  }
}

/** `useSyncExternalStore` snapshot — a number, so React can compare it by identity. */
export function sessionErrorsVersion(): number {
  return version
}

/** @internal Test seam: drop every subscriber and entry so one spec cannot leak into the next. */
export function resetSessionErrors(): void {
  entries = []
  version = 0
  subscribers.clear()
}
